import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { lastValueFrom } from 'rxjs';
import { Project } from 'src/app/models/project.model';
import {
  SlowTransaction,
  Transaction,
  TracingStats,
  TransactionFilters,
} from 'src/app/models/tracing.model';
import { ProjectManagementService } from 'src/app/services/project-management.service';
import { TracingService } from 'src/app/services/tracing.service';

@Component({
  selector: 'app-project-tracing',
  templateUrl: './project-tracing.component.html',
  styleUrls: ['./project-tracing.component.scss'],
  standalone: false
})
export class ProjectTracingPageComponent implements OnInit {

  projectId: string = null;
  project: Project | null = null;

  transactions: Transaction[] = [];
  transactionCount = 0;
  stats: TracingStats | null = null;

  filters: TransactionFilters = { status: '', sort: 'recent', q: '', environment: '', page: 1 };

  loading = true;
  loadingTransactions = false;
  error = false;

  readonly pageSize = 10;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private tracingService: TracingService,
    private projectService: ProjectManagementService,
    private cdr: ChangeDetectorRef
  ) {}

  async ngOnInit() {
    this.projectId = this.route.snapshot.paramMap.get('uuid');

    try {
      const [project, stats] = await Promise.all([
        lastValueFrom(this.projectService.getProject(this.projectId)),
        lastValueFrom(this.tracingService.getStats(this.projectId)),
      ]);
      this.project = project;
      this.stats = stats;
      await this.loadTransactions();
    } catch {
      this.error = true;
    } finally {
      this.loading = false;
      this.cdr.markForCheck();
    }
  }

  async loadTransactions() {
    this.loadingTransactions = true;
    this.cdr.markForCheck();

    try {
      const response = await lastValueFrom(
        this.tracingService.getTransactions(this.projectId, this.filters)
      );
      this.transactions = response.results;
      this.transactionCount = response.count;
    } catch {
      this.error = true;
    } finally {
      this.loadingTransactions = false;
      this.cdr.markForCheck();
    }
  }

  async updateFilter(patch: Partial<TransactionFilters>) {
    this.filters = { ...this.filters, ...patch, page: 1 };
    await this.loadTransactions();
  }

  async filterByName(slow: SlowTransaction) {
    this.filters = { ...this.filters, name: slow.name, page: 1 };
    await this.loadTransactions();
  }

  async clearNameFilter() {
    this.filters = { ...this.filters, name: '', page: 1 };
    await this.loadTransactions();
  }

  async goToPage(page: number) {
    if (page < 1 || page > this.totalPages) {
      return;
    }
    this.filters = { ...this.filters, page };
    await this.loadTransactions();
  }

  get totalPages(): number {
    return Math.max(Math.ceil(this.transactionCount / this.pageSize), 1);
  }

  get currentPage(): number {
    return this.filters.page ?? 1;
  }

  openTransaction(transaction: Transaction) {
    this.router.navigate(['/main', 'project', this.projectId, 'tracing', transaction.uuid]);
  }

  goBack() {
    this.router.navigate(['/main', 'project', this.projectId]);
  }

  // -- formatting ---------------------------------------------------------

  /** Slow requests should look slow: the bar is relative to the slowest one shown. */
  durationShare(duration: number): number {
    const slowest = Math.max(...this.transactions.map(t => t.duration_ms), 1);
    return Math.max((duration / slowest) * 100, 2);
  }

  slowShare(slow: SlowTransaction): number {
    const slowest = Math.max(...(this.stats?.slowest ?? []).map(s => s.avg_ms), 1);
    return Math.max((slow.avg_ms / slowest) * 100, 2);
  }

  formatDuration(ms: number): string {
    if (ms >= 1000) return `${(ms / 1000).toFixed(2)} s`;
    return `${ms.toFixed(0)} ms`;
  }

  relativeTime(value: string): string {
    const diffSeconds = (Date.now() - new Date(value).getTime()) / 1000;

    if (diffSeconds < 60) return 'just now';
    if (diffSeconds < 3600) return `${Math.floor(diffSeconds / 60)}m ago`;
    if (diffSeconds < 86400) return `${Math.floor(diffSeconds / 3600)}h ago`;
    return new Date(value).toLocaleDateString();
  }

  describeUser(transaction: Transaction): string {
    const context = transaction.user_context || {};
    return context['email'] || context['username'] || context['id'] || context['ip_address'] || 'anonymous';
  }
}
